import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Power, Mail, Megaphone, MessageSquare, Newspaper, Mic } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";

const CHANNEL_ICON = { email: Mail, social: Megaphone, sms: MessageSquare, press: Newspaper, podcast: Mic };

export default function MarketingCockpit({ vessel }) {
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", channel: "email", status: "active" });

  const load = async () => {
    const [c, a] = await Promise.all([api.get("/marketing/campaigns"), api.get("/marketing/analytics")]);
    setCampaigns(c.data);
    setAnalytics(a.data);
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => campaigns.filter((c) => c.vessel === vessel), [campaigns, vessel]);

  const totals = visible.reduce(
    (acc, c) => ({
      impressions: acc.impressions + c.impressions,
      engagements: acc.engagements + c.engagements,
      conversions: acc.conversions + c.conversions,
    }),
    { impressions: 0, engagements: 0, conversions: 0 }
  );

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/marketing/campaigns", { ...form, vessel });
      setForm({ name: "", channel: "email", status: "active" });
      setShowForm(false);
      toast.success("Campaign deployed across the Armada.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggle = async (id) => {
    await api.patch(`/marketing/campaigns/${id}/toggle`);
    load();
  };
  const remove = async (id) => {
    await api.delete(`/marketing/campaigns/${id}`);
    toast.success("Campaign struck.");
    load();
  };

  return (
    <div className="space-y-6" data-testid="marketing-cockpit">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono text-[#D8AB4E]">MODULE 04 // MARKETING ENGINE</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-1">Automated Signal Distribution</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">
            Toggle content channels · monitor engagement across the Armada · keep the signal sovereign.
          </p>
        </div>
        <button data-testid="campaign-add-button" onClick={() => setShowForm((v) => !v)} className="btn-gold px-5 py-3 text-[11px] inline-flex items-center gap-2">
          <Plus size={14} /> {showForm ? "CLOSE" : "NEW CAMPAIGN"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Stat label="Impressions · 14d" value={totals.impressions.toLocaleString()} accent="#D8AB4E" />
        <Stat label="Engagements" value={totals.engagements.toLocaleString()} accent="#F2C568" />
        <Stat label="Conversions" value={totals.conversions.toLocaleString()} accent="#C1432E" />
      </div>

      {showForm && (
        <motion.form
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="glass-panel p-6 grid md:grid-cols-12 gap-4"
          data-testid="campaign-form"
        >
          <div className="md:col-span-6">
            <label className="label-mono text-zinc-400 mb-2 block">Campaign name</label>
            <input
              data-testid="campaign-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E]"
              placeholder="Sovereign Memo Q2"
            />
          </div>
          <div className="md:col-span-3">
            <label className="label-mono text-zinc-400 mb-2 block">Channel</label>
            <select
              data-testid="campaign-channel-select"
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E]"
            >
              <option value="email">Email</option>
              <option value="social">Social</option>
              <option value="sms">SMS</option>
              <option value="press">Press</option>
              <option value="podcast">Podcast</option>
            </select>
          </div>
          <div className="md:col-span-3 flex items-end">
            <button data-testid="campaign-submit-button" className="btn-gold w-full py-2.5 text-[11px]">DEPLOY</button>
          </div>
        </motion.form>
      )}

      {/* Analytics chart */}
      {analytics && (
        <div className="glass-panel p-6 relative grain">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono text-[#D8AB4E]">14-DAY ENGAGEMENT</div>
              <div className="font-display text-xl mt-1">Armada-Wide Signal</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={analytics.series}>
                <CartesianGrid stroke="rgba(216,171,78,0.08)" vertical={false} />
                <XAxis dataKey="date" stroke="#52525b" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <YAxis stroke="#52525b" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: "#040404", border: "1px solid rgba(216,171,78,0.4)", fontFamily: "JetBrains Mono", fontSize: 12 }}
                  labelStyle={{ color: "#D8AB4E" }}
                />
                <Line type="monotone" dataKey="impressions" stroke="#D8AB4E" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="engagements" stroke="#F2C568" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="conversions" stroke="#C1432E" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-4 mt-3 font-mono-data text-[10px] text-zinc-400">
            <span className="flex items-center gap-2"><span className="h-2 w-2" style={{ background: "#D8AB4E" }} /> Impressions</span>
            <span className="flex items-center gap-2"><span className="h-2 w-2" style={{ background: "#F2C568" }} /> Engagements</span>
            <span className="flex items-center gap-2"><span className="h-2 w-2" style={{ background: "#C1432E" }} /> Conversions</span>
          </div>
        </div>
      )}

      {/* Campaigns list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visible.length === 0 && (
          <div className="md:col-span-2 glass-panel p-10 text-center text-zinc-500 font-mono-data text-sm">
            No active campaigns on this vessel. Deploy the first one.
          </div>
        )}
        {visible.map((c) => {
          const Icon = CHANNEL_ICON[c.channel] || Megaphone;
          const isActive = c.status === "active";
          return (
            <motion.div
              key={c.id}
              whileHover={{ y: -2 }}
              className="glass-panel p-5 relative grain"
              data-testid={`campaign-${c.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 grid place-items-center border border-[#D8AB4E]/30">
                    <Icon size={14} className="text-[#D8AB4E]" />
                  </div>
                  <div>
                    <div className="font-display text-lg">{c.name}</div>
                    <div className="label-mono text-zinc-500 mt-0.5">
                      {c.channel} · <span style={{ color: isActive ? "#10B981" : "#A1A1AA" }}>{c.status}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`campaign-toggle-${c.id}`}
                    onClick={() => toggle(c.id)}
                    className={`h-8 w-8 grid place-items-center border transition-all ${
                      isActive ? "border-[#10B981]/60 text-[#10B981]" : "border-zinc-700 text-zinc-500"
                    }`}
                    title={isActive ? "Pause" : "Activate"}
                  >
                    <Power size={13} />
                  </button>
                  <button
                    data-testid={`campaign-delete-${c.id}`}
                    onClick={() => remove(c.id)}
                    className="h-8 w-8 grid place-items-center border border-zinc-800 text-zinc-500 hover:text-[#E55A45] hover:border-[#C1432E]/50 transition-all"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-[#D8AB4E]/10">
                <Mini label="Imp." value={c.impressions} color="#D8AB4E" />
                <Mini label="Eng." value={c.engagements} color="#F2C568" />
                <Mini label="Conv." value={c.conversions} color="#C1432E" />
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="glass-panel p-5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: accent, opacity: 0.45 }} />
      <div className="label-mono text-zinc-500">{label}</div>
      <div className="font-mono-data text-2xl mt-2" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Mini({ label, value, color }) {
  return (
    <div>
      <div className="label-mono text-zinc-500">{label}</div>
      <div className="font-mono-data text-sm mt-1" style={{ color }}>{value.toLocaleString()}</div>
    </div>
  );
}
